/**
 * WatchHub — curated halal + educational video discovery, embedded in-app
 * via youtube-nocookie.com (no tracking cookies, no off-app navigation).
 *
 * STRICT WHITELIST — moderation safety:
 *   The list below is the only source of channels/playlists allowed in
 *   AWENI. NEVER add an open YouTube search UI here. Curation only.
 *   When adding a new channel:
 *     1. Verify the channel publishes content compliant with our content
 *        policy (family-safe, Islamic-aligned).
 *     2. Add the channel id + display name + category here.
 *     3. Code reviewer signs off.
 *
 * Categories (rotated by AnticipationEngine score):
 *   - islamic: Bayyinah, Yaqeen Institute, Mufti Menk official
 *   - educational: Veritasium, Kurzgesagt, TED-Ed, National Geographic
 *   - kids: Cocomelon-style + Islamic kids — opt-in only
 *   - documentary: BBC Earth, Discovery
 *
 * Per-tap behavior:
 *   - Tap a thumbnail → opens an in-app sheet with the youtube-nocookie
 *     iframe so the user never leaves AWENI.
 *   - "Open in YouTube" link is hidden by default (only revealed if the
 *     embed fails to load), since the goal is to retain.
 *
 * NB: To preserve performance, we don't fetch live YouTube data. The
 * thumbnails come straight from i.ytimg.com using the videoId — no API
 * key, no rate limits, no PII flow.
 */

import type { Widget, WidgetContext } from '../types';

interface VideoEntry {
  videoId: string;
  title: string; // shown as-is (English; user can recognize creator)
  category: 'islamic' | 'educational' | 'kids' | 'documentary';
  duration?: string; // optional pre-set duration label (e.g. "5:42")
}

// Curated whitelist. Each video must be reviewed before being added.
// IDs are YouTube video IDs (the part after `v=` in the URL). All are
// chosen for evergreen value — no political/news content.
const VIDEOS: VideoEntry[] = [
  // Islamic — Bayyinah / Yaqeen / classical lectures (evergreen)
  { videoId: '7g6kPPe2pVk', title: 'Quran Reflection — Bayyinah TV (sample)', category: 'islamic' },
  { videoId: 'sJ0qnaQNuSk', title: 'The Story of Yusuf — Yaqeen Institute', category: 'islamic' },
  { videoId: '8s31uWfYEnE', title: 'Reflections on Surah Al-Kahf', category: 'islamic' },
  // Educational — Veritasium / TED-Ed (evergreen)
  { videoId: 'sgQVjTV_RLs', title: 'How One Line in the Oldest Math Text Hinted at Hidden Universes — Veritasium', category: 'educational' },
  { videoId: 'kts5G2OQj-Y', title: 'How does the stock market work? — TED-Ed', category: 'educational' },
  { videoId: 'qsMkZW9TAzU', title: 'Why some people are more altruistic — TED', category: 'educational' },
  // Kids — children's stories (curated)
  { videoId: 'V43XHj-FAtw', title: 'The Quran Stories — kids edition (sample)', category: 'kids' },
  // Documentary — BBC Earth (evergreen nature)
  { videoId: 'TXvE4yeyOPc', title: 'Africa — BBC Earth (sample)', category: 'documentary' },
];

const CATEGORIES: Array<{ id: string; labelKey: string; emoji: string }> = [
  { id: 'islamic', labelKey: 'watch_cat_islamic', emoji: '📿' },
  { id: 'educational', labelKey: 'watch_cat_educational', emoji: '🎓' },
  { id: 'documentary', labelKey: 'watch_cat_documentary', emoji: '🌍' },
  { id: 'kids', labelKey: 'watch_cat_kids', emoji: '🧒' },
];

function thumbnailUrl(videoId: string): string {
  // hqdefault is reliably available for every video — mqdefault smaller.
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

export const WatchHub: Widget = {
  id: 'watch-hub',
  label: {
    en: 'Watch',
    fr: 'Regarder',
    ar: 'شاهد',
    es: 'Ver',
    de: 'Ansehen',
  },
  icon: '📺',
  defaultVisible: true,
  defaultOrder: 27,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    let activeCat = 'islamic';

    const renderShell = (): void => {
      const filtered = VIDEOS.filter((v) => v.category === activeCat).slice(0, 6);
      container.innerHTML = `
        <div style="background:var(--ln-bg-card,var(--card-bg,#fff));border:1px solid var(--ln-border,var(--border,rgba(0,0,0,0.06)));border-radius:16px;padding:14px;">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
            <span style="font-size:18px;">📺</span>
            <span style="font-size:13px;font-weight:700;">${escapeHtml(t('widget_watch_title') || 'Watch')}</span>
            <span style="margin-inline-start:auto;font-size:10px;color:var(--ln-text-secondary,#6b6657);text-transform:uppercase;letter-spacing:0.4px;">${escapeHtml(t('widget_watch_curated') || 'Curated')}</span>
          </div>

          <div style="display:flex;gap:6px;overflow-x:auto;margin-bottom:10px;padding-bottom:4px;-webkit-overflow-scrolling:touch;">
            ${CATEGORIES.map((c) => `
              <button type="button" data-cat="${c.id}"
                style="flex-shrink:0;padding:6px 10px;border-radius:999px;border:1px solid ${c.id === activeCat ? 'var(--ln-accent,#0A0A0A)' : 'var(--ln-border,rgba(0,0,0,0.10))'};background:${c.id === activeCat ? 'var(--ln-accent,#0A0A0A)' : 'transparent'};color:${c.id === activeCat ? 'var(--ln-accent-text,#fff)' : 'inherit'};font-size:11px;font-weight:600;cursor:pointer;white-space:nowrap;">
                ${c.emoji} ${escapeHtml(t(c.labelKey) || c.id)}
              </button>
            `).join('')}
          </div>

          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
            ${filtered.map((v) => `
              <button type="button" data-video="${escapeAttr(v.videoId)}" data-title="${escapeAttr(v.title)}"
                style="border:none;padding:0;background:transparent;cursor:pointer;text-align:start;color:inherit;">
                <div style="position:relative;border-radius:10px;overflow:hidden;background:#0001;aspect-ratio:16/9;">
                  <img loading="lazy" src="${escapeAttr(thumbnailUrl(v.videoId))}" alt="" style="width:100%;height:100%;object-fit:cover;display:block;" />
                  <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.18);">
                    <span style="width:40px;height:40px;border-radius:999px;background:rgba(255,255,255,0.9);display:inline-flex;align-items:center;justify-content:center;font-size:16px;color:#0A0A0A;">▶</span>
                  </div>
                </div>
                <div style="font-size:12px;font-weight:600;line-height:1.35;margin-top:6px;color:var(--ln-text-primary,#1E293B);overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;">${escapeHtml(v.title)}</div>
              </button>
            `).join('')}
          </div>
        </div>
      `;

      container.querySelectorAll<HTMLButtonElement>('[data-cat]').forEach((b) => b.addEventListener('click', () => {
        activeCat = b.dataset.cat || activeCat;
        renderShell();
      }));
      container.querySelectorAll<HTMLButtonElement>('[data-video]').forEach((b) => b.addEventListener('click', () => {
        const id = b.dataset.video || '';
        const title = b.dataset.title || '';
        if (id) openVideoSheet(id, title, ctx);
      }));
    };
    renderShell();
  },
};

function openVideoSheet(videoId: string, title: string, ctx: WidgetContext): void {
  const overlay = document.createElement('div');
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:60;animation:askfabOverlayIn var(--motion-duration-base) var(--motion-easing-default);';
  overlay.addEventListener('click', () => { overlay.remove(); sheet.remove(); });

  const sheet = document.createElement('div');
  sheet.style.cssText = `position:fixed;inset-inline:0;bottom:0;max-height:90vh;background:#000;color:#fff;border-top-left-radius:20px;border-top-right-radius:20px;z-index:61;animation:askfabSheetIn var(--motion-duration-base) var(--motion-easing-emphasized);overflow:hidden;`;
  sheet.setAttribute('dir', ctx.isRtl ? 'rtl' : 'ltr');

  // youtube-nocookie embed: zero tracking, plays inside AWENI shell.
  const embedUrl = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;

  sheet.innerHTML = `
    <div style="display:flex;align-items:center;gap:12px;padding:10px 14px;">
      <button type="button" data-act="close" aria-label="${escapeAttr(ctx.t('common_close') || 'Close')}"
        style="width:36px;height:36px;border-radius:999px;border:none;background:rgba(255,255,255,0.18);color:#fff;cursor:pointer;font-size:18px;">✕</button>
      <div style="flex:1;font-size:13px;font-weight:600;line-height:1.3;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;">${escapeHtml(title)}</div>
    </div>
    <div style="position:relative;width:100%;aspect-ratio:16/9;background:#000;">
      <iframe src="${escapeAttr(embedUrl)}" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen
        style="position:absolute;inset:0;width:100%;height:100%;border:none;"></iframe>
    </div>
  `;

  sheet.querySelector('[data-act="close"]')?.addEventListener('click', () => { overlay.remove(); sheet.remove(); });
  document.body.appendChild(overlay);
  document.body.appendChild(sheet);
}

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
